// Hook for the user's subscription tier and item/group limits.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Mirrors a row from the `subscriptions` table.
 *
 * [LEARN] Stripe fields (stripe_customer_id, stripe_subscription_id, current_period_start/end)
 * are stored for billing reconciliation only — the frontend uses only `tier` and grace period
 * fields. This avoids tight coupling to Stripe's async webhook updates.
 */
export interface Subscription {
  id: string;
  user_id: string;
  tier: 'free' | 'paid';
  /** Maximum number of items the user can create (enforced by DB trigger). */
  max_items: number;
  /** Maximum number of groups the user can create or join (enforced by DB trigger). */
  max_groups: number;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  /** Timestamp when the user's subscription was downgraded from paid to free. */
  downgraded_at: string | null;
  /** When the 7-day grace period ends and items will be frozen. Null if no freeze pending. */
  grace_period_ends_at: string | null;
  /** When the user's free trial ends. Null if not on trial or trial has ended. */
  trial_ends_at: string | null;
}

/**
 * Fallback limits applied when the user has no `subscriptions` row yet.
 * These are hard limits enforced at the DB layer (triggers/RPCs) — the
 * frontend uses them only for informational UI (progress bars, upgrade prompts).
 */
const FREE_DEFAULTS: Pick<Subscription, 'tier' | 'max_items' | 'max_groups'> = {
  tier: 'free',
  max_items: 10,
  max_groups: 1,
};

/**
 * Returns over-limit status for a free-tier user.
 *
 * [LEARN] Over-limit vs at-limit are different states with different UI messaging.
 * At-limit is normal ("you've used all 10 slots"). Over-limit only happens after a
 * paid→free downgrade when the user had more items than the free cap — it triggers
 * freeze warnings instead of a simple "limit reached" message.
 */
export function getOverLimitStatus(
  tier: 'free' | 'paid',
  itemCount: number,
  maxItems: number,
  groupCount: number,
  maxGroups: number,
) {
  const isFree = tier === 'free';
  return {
    isItemsOverLimit: isFree && maxItems > 0 && itemCount > maxItems,
    isGroupsOverLimit: isFree && maxGroups > 0 && groupCount > maxGroups,
  };
}

/**
 * Fetches the current user's subscription tier and item/group limits.
 *
 * New users may not have a row in the `subscriptions` table yet, so
 * `FREE_DEFAULTS` are used as a fallback to avoid blocking the UI.
 * Uses `.maybeSingle()` which returns `null` (not an error) when no row
 * is found. Cache has a 1-hour `staleTime` since tier changes are infrequent;
 * a mid-session upgrade in another tab will not reflect until the cache
 * expires or the page is refreshed.
 *
 * @returns `{ subscription, isLoading, tier, maxItems, maxGroups, isPaid, isInGracePeriod, gracePeriodEndsAt }`
 *   with free-tier defaults applied when no subscription row exists.
 */
export function useSubscription() {
  const { user } = useAuth();

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        // [LEARN] .maybeSingle() returns null when no row is found, unlike .single() which throws an error — safe for new users with no subscription row yet.
        .maybeSingle();

      if (error) throw error;

      if (!data) return null;

      return data as Subscription;
    },
    enabled: !!user,
    // 1-hour stale time: subscription tier changes infrequently and a short
    // delay is acceptable.  Note: a mid-session upgrade in another tab will not
    // be reflected until the cache expires or the page is refreshed.
    // [LEARN] staleTime tells React Query to treat this data as fresh for 1 hour — it won't refetch during that window since subscription tier rarely changes.
    staleTime: 60 * 60 * 1000,
  });

  const gracePeriodEndsAt = subscription?.grace_period_ends_at ?? null;
  const isInGracePeriod =
    !!gracePeriodEndsAt && new Date(gracePeriodEndsAt) > new Date();

  const trialEndsAt = subscription?.trial_ends_at ?? null;
  const isOnTrial =
    !!trialEndsAt && new Date(trialEndsAt) > new Date() && subscription?.tier === 'paid';
  const trialDaysRemaining = isOnTrial
    ? Math.max(0, Math.ceil((new Date(trialEndsAt!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  // Expose limits directly so callers don't need to handle the null case.
  // Free-tier defaults apply when no subscription row exists.
  // [LEARN] When tier is 'free', enforce free-tier caps even if DB still has -1
  // (unlimited). This guards against inconsistent state from manual DB edits or
  // webhook issues during paid→free downgrade.
  const effectiveTier = subscription?.tier ?? FREE_DEFAULTS.tier;
  const rawMaxItems = subscription?.max_items ?? FREE_DEFAULTS.max_items;
  const rawMaxGroups = subscription?.max_groups ?? FREE_DEFAULTS.max_groups;
  const maxItems = effectiveTier === 'free' && rawMaxItems === -1 ? FREE_DEFAULTS.max_items : rawMaxItems;
  const maxGroups = effectiveTier === 'free' && rawMaxGroups === -1 ? FREE_DEFAULTS.max_groups : rawMaxGroups;

  return {
    subscription,
    isLoading,
    tier: effectiveTier,
    maxItems,
    maxGroups,
    isPaid: subscription?.tier === 'paid',
    isInGracePeriod,
    gracePeriodEndsAt,
    isOnTrial,
    trialDaysRemaining,
    trialEndsAt,
  };
}
